import * as React from 'react'
import {
  FaIcon,
  faXmark,
  faIdBadge,
  faCircleInfo,
  faArrowDownWideShort,
} from '@/lib/fa'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { Input } from '@/ui/form/primitives/Input'
import { FieldsetRadio } from '@/modules/settings/components/FieldsetRadio'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import { v4 as uuidv4 } from 'uuid'
import type { BonusConfigurationModel } from '@/app/models/timesheet/bonusConfigurationModel'
import { TaskType } from '@/app/constants/taskType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'

interface DriverFormData {
  oczekiwaneNetto: string
  urlopNb: string
  choroboweNb: string
}

interface CalculateRecordsModalProps {
  isOpen: boolean
  onClose: () => void
  companyId: string
  selectedMonth: string // Format: "MM-YYYY"
  onCalculateSuccess?: () => void
}

const CalculateRecordsModalComponent: React.FC<CalculateRecordsModalProps> = ({
  isOpen,
  onClose,
  companyId,
  selectedMonth,
  onCalculateSuccess,
}) => {
  const { drivers } = useDrivers()
  const { t } = useLocalize()
  const { calculateTimesheetLegacy, precalculateTimesheet, importRewardFile } =
    useTimesheetService()
  const [activeTab, setActiveTab] = React.useState<'drivers' | 'settings'>(
    'drivers',
  )
  const [selectedDriverIds, setSelectedDriverIds] = React.useState<Set<number>>(
    new Set(),
  )
  const [driverFormData, setDriverFormData] = React.useState<
    Map<number, DriverFormData>
  >(new Map())
  const [isCalculating, setIsCalculating] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Settings state
  const [automaticOvertimeCompensation, setAutomaticOvertimeCompensation] =
    React.useState(true)
  const [zusPaidFromAverageGross, setZusPaidFromAverageGross] =
    React.useState(false)
  const [includeDelegationsInNetAmount, setIncludeDelegationsInNetAmount] =
    React.useState(true)
  const [delegationDateRange, setDelegationDateRange] = React.useState<
    'start' | 'end'
  >('start')

  // Initialize form data for all drivers
  React.useEffect(() => {
    if (isOpen && drivers.length > 0) {
      const initialData = new Map<number, DriverFormData>()
      drivers.forEach((driver) => {
        if (!driverFormData.has(driver.id)) {
          initialData.set(driver.id, {
            oczekiwaneNetto: '',
            urlopNb: '',
            choroboweNb: '',
          })
        }
      })
      setDriverFormData((prev) => {
        const updated = new Map(prev)
        initialData.forEach((value, key) => {
          updated.set(key, value)
        })
        return updated
      })
    }
  }, [isOpen, drivers])

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedDriverIds(new Set())
      setDriverFormData(new Map())
      setActiveTab('drivers')
      // Reset settings to defaults
      setAutomaticOvertimeCompensation(true)
      setZusPaidFromAverageGross(false)
      setIncludeDelegationsInNetAmount(true)
      setDelegationDateRange('start')
    }
  }, [isOpen])

  const handleDriverToggle = (driverId: number) => {
    setSelectedDriverIds((prev) => {
      const updated = new Set(prev)
      if (updated.has(driverId)) {
        updated.delete(driverId)
      } else {
        updated.add(driverId)
      }
      return updated
    })
  }

  const handleSelectAll = () => {
    if (selectedDriverIds.size === drivers.length) {
      setSelectedDriverIds(new Set())
    } else {
      setSelectedDriverIds(new Set(drivers.map((d) => d.id)))
    }
  }

  const handleInputChange = (
    driverId: number,
    field: keyof DriverFormData,
    value: string,
  ) => {
    setDriverFormData((prev) => {
      const updated = new Map(prev)
      const current = updated.get(driverId) || {
        oczekiwaneNetto: '',
        urlopNb: '',
        choroboweNb: '',
      }
      updated.set(driverId, { ...current, [field]: value })
      return updated
    })
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      await importRewardFile({ companyId, file })
      // TODO: Handle success - maybe refresh driver data or show toast
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      console.error('Error importing file:', error)
      // TODO: Show error toast
    }
  }

  // Helper function to parse numeric value from string, handling Polish number format
  const parseNumericValue = (value: string): number | undefined => {
    if (!value || value.trim() === '') return undefined
    // Remove spaces and replace comma with dot for decimal parsing
    const cleaned = value.trim().replace(/\s/g, '').replace(',', '.')
    // Handle formats like "value1 / value2" or "value1/value2" - take first value
    const firstPart = cleaned.split(/[\/]/)[0]
    const parsed = parseFloat(firstPart)
    return isNaN(parsed) ? undefined : parsed
  }

  // Build bonusConfigurations from driver form data
  const buildBonusConfigurations = (): BonusConfigurationModel[] => {
    const configurations: BonusConfigurationModel[] = []

    selectedDriverIds.forEach((driverId) => {
      const formData = driverFormData.get(driverId)
      if (!formData) return

      const config: BonusConfigurationModel = {
        driverId: String(driverId),
      }

      // Map oczekiwaneNetto to expectedNettoAmount
      if (formData.oczekiwaneNetto) {
        const parsed = parseNumericValue(formData.oczekiwaneNetto)
        if (parsed !== undefined) {
          config.expectedNettoAmount = parsed
        }
      }

      // Map urlopNb to leavesNettoAmount
      if (formData.urlopNb) {
        const parsed = parseNumericValue(formData.urlopNb)
        if (parsed !== undefined) {
          config.leavesNettoAmount = parsed
        }
      }

      // Map choroboweNb to sickLeavesNettoAmount
      if (formData.choroboweNb) {
        const parsed = parseNumericValue(formData.choroboweNb)
        if (parsed !== undefined) {
          config.sickLeavesNettoAmount = parsed
        }
      }

      // Only add configuration if it has at least one value
      if (
        config.expectedNettoAmount !== undefined ||
        config.leavesNettoAmount !== undefined ||
        config.sickLeavesNettoAmount !== undefined
      ) {
        configurations.push(config)
      }
    })

    return configurations
  }

  const handleCalculate = async () => {
    if (selectedDriverIds.size === 0) return

    setIsCalculating(true)
    try {
      // Parse selected month (MM-YYYY) to extract month and year
      const [monthStr, yearStr] = selectedMonth.split('-')
      const month = parseInt(monthStr, 10)
      const year = parseInt(yearStr, 10)

      if (isNaN(month) || isNaN(year)) {
        console.error('Invalid month format:', selectedMonth)
        // TODO: Show error toast
        return
      }

      const drivers = Array.from(selectedDriverIds)
      const requestId = uuidv4()

      // Step 1: Call precalculate first
      const precalculateResult = await precalculateTimesheet({
        companyId,
        drivers,
        months: [month],
        year,
        outOfDateQuarterExchangeRateAccepted: false,
        id: requestId,
      })

      // Check if precalculate returned errors
      // Legacy API returns { error: [...], type: 'ERROR' | 'WARNING' } on error
      // Can calculate if: result exists AND (no error OR error type is not ERROR)
      const canCalculate =
        precalculateResult &&
        (precalculateResult.error == null ||
          precalculateResult.type !== 'ERROR')

      if (precalculateResult?.error && precalculateResult.type === 'ERROR') {
        console.error('Precalculate error:', precalculateResult.error)
        // TODO: Show error toast with precalculateResult.error
        setIsCalculating(false)
        return
      }

      // If precalculate has warnings but not errors, we can still proceed
      if (precalculateResult?.error && precalculateResult.type === 'WARNING') {
        console.warn('Precalculate warnings:', precalculateResult.error)
        // TODO: Show warning toast
      }

      // Step 2: If precalculate succeeded, call calculate with bonusConfigurations
      if (canCalculate) {
        const bonusConfigurations = buildBonusConfigurations()

        try {
          // 409 = ten miesiac juz sie liczy. Przejmujemy biegnace zadanie zamiast
          // zamykac modal po cichu z bledem w konsoli.
          const { adopted } = await startTrackedTask({
            companyId,
            type: TaskType.Timesheet,
            taskId: requestId,
            scopeKey: taskScopeKey.timesheet(companyId, year, [month]),
            start: () =>
              calculateTimesheetLegacy({
                companyId,
                drivers,
                months: [month],
                year,
                outOfDateQuarterExchangeRateAccepted: false,
                bonusConfigurations,
                id: requestId,
              }),
          })

          if (adopted) {
            // Zakres jest po okresie, nie po zaznaczeniu - biegnace zadanie moze
            // obejmowac innych kierowcow niz ci wybrani w modalu.
            toast.info({
              title: t('timesheet.calculation-in-progress'),
              text: t('timesheet.calculation-adopted-other-selection'),
            })
          }

          onCalculateSuccess?.()
          onClose()
        } catch (calculateError) {
          if (isTaskRateLimited(calculateError)) {
            toast.warning({
              title: t('timesheet.calculation-in-progress'),
              text: t('timesheet.too-many-tasks'),
            })
            setIsCalculating(false)
            return
          }
          // If calculate fails, show error and don't add task
          console.error('Error calculating timesheet:', calculateError)
          // TODO: Show error toast
          setIsCalculating(false)
        }
      }
    } catch (error) {
      console.error('Error calculating timesheet:', error)
      // TODO: Show error toast
    } finally {
      setIsCalculating(false)
    }
  }

  if (!isOpen) return null

  const selectedCount = selectedDriverIds.size
  const totalDrivers = drivers.length

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-[#1f2937] tw-bg-opacity-70">
      <div className="tw-w-full tw-max-w-[967px] tw-rounded-lg tw-bg-white tw-shadow-lg">
        {/* Header */}
        <div className="tw-flex tw-items-start tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-p-6">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <h2 className="tw-text-xl tw-font-semibold tw-text-[#1f2937]">
              {t('timesheet.calculate-timesheet')}
            </h2>
            <p className="tw-text-sm tw-text-[#1f2937]">
              {t('timesheet.calculate-timesheet-description')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
            aria-label="Close"
          >
            <FaIcon
              icon={faXmark}
              className="tw-h-5 tw-w-5 tw-text-[#1f2937]"
            />
          </button>
        </div>

        {/* Tabs */}
        <div className="tw-flex tw-border-b tw-border-[#e5e6e6] tw-bg-gradient-to-b tw-from-white tw-to-[#fafbff]">
          <button
            onClick={() => setActiveTab('drivers')}
            className={`tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-transition-colors ${
              activeTab === 'drivers'
                ? 'tw-border-b-2 tw-border-[#2448bb] tw-font-semibold tw-text-[#2448bb]'
                : 'tw-text-[#1f2937] hover:tw-bg-gray-50'
            }`}
          >
            {t('timesheet.tab-drivers')}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`tw-flex tw-items-center tw-justify-center tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-transition-colors ${
              activeTab === 'settings'
                ? 'tw-border-b-2 tw-border-[#2448bb] tw-font-semibold tw-text-[#2448bb]'
                : 'tw-text-[#1f2937] hover:tw-bg-gray-50'
            }`}
          >
            {t('timesheet.tab-settings')}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'drivers' && (
          <div className="tw-max-h-[400px] tw-overflow-y-auto">
            {/* Table Header */}
            <div className="tw-sticky tw-top-0 tw-z-10 tw-grid tw-grid-cols-[40px_240px_240px_240px_207px] tw-border-b tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-25">
              <div className="tw-flex tw-items-center tw-justify-center tw-p-2">
                <Checkbox
                  checked={
                    selectedDriverIds.size === drivers.length &&
                    drivers.length > 0
                  }
                  onChange={handleSelectAll}
                  className="tw-h-4 tw-w-4"
                />
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                <span className="tw-text-xs tw-font-medium tw-text-[#1f2937]">
                  {t('timesheet.col-driver-name')}
                </span>
                <FaIcon
                  icon={faArrowDownWideShort}
                  className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
                />
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                <span className="tw-text-xs tw-font-medium tw-text-[#1f2937]">
                  <span className="tw-text-[#e03a3e]">*</span> {t('timesheet.col-expected-netto')}
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                <span className="tw-text-xs tw-font-medium tw-text-[#1f2937]">
                  {t('timesheet.col-vacation-nb')}
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                <span className="tw-text-xs tw-font-medium tw-text-[#1f2937]">
                  {t('timesheet.col-sick-leave-nb')}
                </span>
              </div>
            </div>

            {/* Table Rows */}
            <div className="tw-divide-y tw-divide-[#e5e6e6]">
              {drivers.map((driver) => {
                const isSelected = selectedDriverIds.has(driver.id)
                const formData = driverFormData.get(driver.id) || {
                  oczekiwaneNetto: '',
                  urlopNb: '',
                  choroboweNb: '',
                }

                return (
                  <div
                    key={driver.id}
                    className="tw-grid tw-grid-cols-[40px_240px_240px_240px_207px] tw-border-b tw-border-[#e5e6e6] hover:tw-bg-gray-50"
                  >
                    <div className="tw-flex tw-items-center tw-justify-center tw-p-2">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleDriverToggle(driver.id)}
                        className="tw-h-4 tw-w-4"
                      />
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                      <FaIcon
                        icon={faIdBadge}
                        className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
                      />
                      <span className="tw-text-xs tw-text-[#1f2937]">
                        {driver.surname} {driver.name}
                      </span>
                    </div>
                    <div className="tw-flex tw-items-center tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                      <Input
                        value={formData.oczekiwaneNetto}
                        onChange={(e) =>
                          handleInputChange(
                            driver.id,
                            'oczekiwaneNetto',
                            e.target.value,
                          )
                        }
                        placeholder={t('timesheet.fill-in')}
                        className="tw-w-full tw-rounded tw-border tw-border-[#e5e6e6] tw-px-2 tw-py-1 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-[#2448bb]"
                      />
                    </div>
                    <div className="tw-flex tw-items-center tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                      <Input
                        value={formData.urlopNb}
                        onChange={(e) =>
                          handleInputChange(
                            driver.id,
                            'urlopNb',
                            e.target.value,
                          )
                        }
                        placeholder={t('timesheet.fill-in')}
                        className="tw-w-full tw-rounded tw-border tw-border-[#e5e6e6] tw-px-2 tw-py-1 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-[#2448bb]"
                      />
                    </div>
                    <div className="tw-flex tw-items-center tw-px-3 tw-py-2">
                      <Input
                        value={formData.choroboweNb}
                        onChange={(e) =>
                          handleInputChange(
                            driver.id,
                            'choroboweNb',
                            e.target.value,
                          )
                        }
                        placeholder={t('timesheet.fill-in')}
                        className="tw-w-full tw-rounded tw-border tw-border-[#e5e6e6] tw-px-2 tw-py-1 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-[#2448bb]"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tw-p-6">
            <div className="tw-flex tw-gap-8">
              {/* Ogólne Section */}
              <div className="tw-flex tw-flex-col tw-gap-6">
                <h3 className="tw-text-xs tw-font-medium tw-text-[#1f2937] tw-opacity-80">
                  {t('timesheet.settings-general')}
                </h3>
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {/* Automatyczna rekompensata nadgodzin */}
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <label className="tw-relative tw-inline-block tw-h-6 tw-w-10 tw-cursor-pointer">
                      <input
                        type="checkbox"
                        checked={automaticOvertimeCompensation}
                        onChange={(e) =>
                          setAutomaticOvertimeCompensation(e.target.checked)
                        }
                        className="tw-sr-only"
                      />
                      <span
                        className={`tw-block tw-h-6 tw-w-10 tw-rounded-2xl tw-transition-colors tw-duration-200 ${
                          automaticOvertimeCompensation
                            ? 'tw-bg-[#2448bb]'
                            : 'tw-bg-[#ccd5f6]'
                        }`}
                      >
                        <span
                          className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-transition-transform tw-duration-200 ${
                            automaticOvertimeCompensation
                              ? 'tw-translate-x-4'
                              : 'tw-translate-x-0'
                          }`}
                        />
                      </span>
                    </label>
                    <span className="tw-cursor-pointer tw-text-xs tw-text-[#1f2937]">
                      {t('timesheet.automatic-overtime-compensation')}
                    </span>
                  </div>

                  {/* ZUS płatny od przeciętnej brutto */}
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <label className="tw-relative tw-inline-block tw-h-6 tw-w-10 tw-cursor-pointer">
                      <input
                        type="checkbox"
                        checked={zusPaidFromAverageGross}
                        onChange={(e) =>
                          setZusPaidFromAverageGross(e.target.checked)
                        }
                        className="tw-sr-only"
                      />
                      <span
                        className={`tw-block tw-h-6 tw-w-10 tw-rounded-2xl tw-transition-colors tw-duration-200 ${
                          zusPaidFromAverageGross
                            ? 'tw-bg-[#2448bb]'
                            : 'tw-bg-[#ccd5f6]'
                        }`}
                      >
                        <span
                          className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-transition-transform tw-duration-200 ${
                            zusPaidFromAverageGross
                              ? 'tw-translate-x-4'
                              : 'tw-translate-x-0'
                          }`}
                        />
                      </span>
                    </label>
                    <span className="tw-cursor-pointer tw-text-xs tw-text-[#1f2937]">
                      {t('timesheet.zus-from-average-gross')}
                    </span>
                  </div>

                  {/* Wliczaj delegacje do kwoty netto */}
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <label className="tw-relative tw-inline-block tw-h-6 tw-w-10 tw-cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeDelegationsInNetAmount}
                        onChange={(e) =>
                          setIncludeDelegationsInNetAmount(e.target.checked)
                        }
                        className="tw-sr-only"
                      />
                      <span
                        className={`tw-block tw-h-6 tw-w-10 tw-rounded-2xl tw-transition-colors tw-duration-200 ${
                          includeDelegationsInNetAmount
                            ? 'tw-bg-[#2448bb]'
                            : 'tw-bg-[#ccd5f6]'
                        }`}
                      >
                        <span
                          className={`tw-absolute tw-left-0.5 tw-top-0.5 tw-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-transition-transform tw-duration-200 ${
                            includeDelegationsInNetAmount
                              ? 'tw-translate-x-4'
                              : 'tw-translate-x-0'
                          }`}
                        />
                      </span>
                    </label>
                    <span className="tw-cursor-pointer tw-text-xs tw-text-[#1f2937]">
                      {t('timesheet.include-delegations-in-netto')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delegacje Section */}
              <div className="tw-flex tw-flex-col tw-gap-6">
                <FieldsetRadio
                  label={t('timesheet.delegations')}
                  options={[
                    {
                      key: 'start',
                      value: 'start',
                      label: t('timesheet.delegations-starting-in-range'),
                    },
                    {
                      key: 'end',
                      value: 'end',
                      label: t('timesheet.delegations-ending-in-range'),
                    },
                  ]}
                  value={delegationDateRange}
                  handleChange={(_, { option }) =>
                    setDelegationDateRange(option.key as 'start' | 'end')
                  }
                  name="delegationDateRange"
                />
              </div>
            </div>
          </div>
        )}

        {/* Info Section */}
        {activeTab === 'drivers' && (
          <div className="tw-border-t tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-50 tw-px-6 tw-py-3">
            <div className="tw-flex tw-items-start tw-gap-2">
              <FaIcon
                icon={faCircleInfo}
                className="tw-mt-0.5 tw-h-4 tw-w-4 tw-text-[#1f2937]"
              />
              <p className="tw-text-xs tw-leading-4 tw-text-[#1f2937]">
                {t('timesheet.calculate-info-line1')}
                <br />
                {t('timesheet.calculate-info-line2')}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-[#1f2937]">
              <span>{t('timesheet.selected')}</span>
              <span className="tw-font-semibold">
                {selectedCount}/{totalDrivers}
              </span>
              <span>{t('timesheet.drivers-count')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="tw-hidden"
                accept=".xlsx,.xls,.csv"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="tw-h-[38px] tw-rounded-lg"
              >
                {activeTab === 'settings'
                  ? t('timesheet.import-from-file')
                  : t('timesheet.import-more-from-file')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCalculate}
                disabled={selectedCount === 0 || isCalculating}
                loading={isCalculating}
                className="tw-h-[38px] tw-rounded-lg"
              >
                {t('timesheet.calculate-timesheet')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const CalculateRecordsModal = CalculateRecordsModalComponent

export default CalculateRecordsModal
